/**
 * Warna utama etalase sebagai TEKS di tema gelap (ADR-0020 keputusan 5). Logika murni, dipakai
 * halaman tamu dan pratinjau di konsol.
 *
 * Server hanya menerima warna utama yang kontrasnya minimal 4,5:1 terhadap teks putih, jadi
 * hampir semua warna pemilik gelap. Di atas latar tema gelap warna itu tidak terbaca sebagai teks
 * (harga, tautan, angka bayar). Tombol tetap memakai warna aslinya; yang dicerahkan hanya teksnya:
 * terang (L di OKLCH) dinaikkan sedikit demi sedikit, rona dan kroma dipertahankan sejauh masih
 * di dalam gamut sRGB, sampai kontrasnya minimal 4,5:1.
 */

/** Permukaan paling terang tema gelap (`surface-tertiary`), latar teks aksen yang paling sulit. */
export const LATAR_TEKS_GELAP = "#2B2F29"

const BATAS = 4.5

type Rgb = readonly [number, number, number]

const keLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
const keGamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)

const dariHex = (hex: string): Rgb => [
  Number.parseInt(hex.slice(1, 3), 16) / 255,
  Number.parseInt(hex.slice(3, 5), 16) / 255,
  Number.parseInt(hex.slice(5, 7), 16) / 255,
]

const keHex = (rgb: Rgb): string =>
  `#${rgb
    .map((c) =>
      Math.round(Math.min(1, Math.max(0, c)) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase()}`

const luminans = (hex: string): number => {
  const [r, g, b] = dariHex(hex).map(keLinear) as unknown as Rgb
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Kontras WCAG 2.x antara dua hex 6 digit. */
export const kontras = (a: string, b: string): number => {
  const [terang, gelap] = [luminans(a), luminans(b)].sort((x, y) => y - x) as [number, number]
  return (terang + 0.05) / (gelap + 0.05)
}

const keOklch = (hex: string): Rgb => {
  const [r, g, b] = dariHex(hex).map(keLinear) as unknown as Rgb
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  return [L, Math.hypot(A, B), Math.atan2(B, A)]
}

/** sRGB gamma dari OKLCH; `null` kalau di luar gamut. */
const dariOklch = (L: number, C: number, h: number): Rgb | null => {
  const A = C * Math.cos(h)
  const B = C * Math.sin(h)
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3
  const linear = [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
  if (linear.some((c) => c < -1e-4 || c > 1 + 1e-4)) return null
  return linear.map((c) => keGamma(Math.min(1, Math.max(0, c)))) as unknown as Rgb
}

/** Kroma terbesar (sampai kroma asal) yang masih di dalam gamut pada terang dan rona ini. */
const dalamGamut = (L: number, C: number, h: number): Rgb => {
  const utuh = dariOklch(L, C, h)
  if (utuh) return utuh
  let bawah = 0
  let atas = C
  for (let i = 0; i < 20; i++) {
    const tengah = (bawah + atas) / 2
    if (dariOklch(L, tengah, h)) bawah = tengah
    else atas = tengah
  }
  return dariOklch(L, bawah, h) ?? [L, L, L]
}

/**
 * Warna teks untuk tema gelap dari warna utama `hex` (hex 6 digit). Warna yang sudah cukup terang
 * dikembalikan apa adanya.
 */
export const warnaTeksGelap = (hex: string, latar: string = LATAR_TEKS_GELAP): string => {
  const asal = hex.toUpperCase()
  if (kontras(asal, latar) >= BATAS) return asal
  const [L, C, h] = keOklch(asal)
  for (let terang = L + 0.005; terang <= 1; terang += 0.005) {
    const calon = keHex(dalamGamut(terang, C, h))
    if (kontras(calon, latar) >= BATAS) return calon
  }
  return "#FFFFFF"
}
